import { useCallback, useEffect, useRef, useState } from 'react';
import { mondayOf, weekEnd } from '../../utils/recognitionWeeks';

export type ReviewQueueDateMode = 'week' | 'day' | 'range';

export interface ReviewQueueDateSelection {
  mode: ReviewQueueDateMode;
  weekStart: string;
  capturedDate: string | null;
  capturedDateTo: string | null;
}

function formatDate(value: string): string {
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function selectionLabel(selection: ReviewQueueDateSelection): string {
  const end = weekEnd(selection.weekStart) ?? selection.weekStart;
  if (selection.mode === 'day' && selection.capturedDate) {
    return `Day · ${formatDate(selection.capturedDate)}`;
  }
  if (selection.mode === 'range' && selection.capturedDate && selection.capturedDateTo) {
    return `Range · ${formatDate(selection.capturedDate)}–${formatDate(selection.capturedDateTo)}`;
  }
  return `Week · ${formatDate(selection.weekStart)}–${formatDate(end)}`;
}

/** One queue-scoped control for choosing a whole week, one day, or an inclusive date range. */
export function ReviewQueueDatePicker({
  value,
  onChange,
}: {
  value: ReviewQueueDateSelection;
  onChange: (selection: ReviewQueueDateSelection) => void;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [draftMode, setDraftMode] = useState(value.mode);
  const [draftStart, setDraftStart] = useState(value.capturedDate ?? value.weekStart);
  const [draftEnd, setDraftEnd] = useState(
    value.capturedDateTo ?? value.capturedDate ?? value.weekStart
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstDialogControlRef = useRef<HTMLButtonElement>(null);
  const shouldRestoreFocusRef = useRef(false);

  const closePicker = useCallback((restoreFocus = true): void => {
    shouldRestoreFocusRef.current = restoreFocus;
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (isOpen) return;
    setDraftMode(value.mode);
    setDraftStart(value.capturedDate ?? value.weekStart);
    setDraftEnd(value.capturedDateTo ?? value.capturedDate ?? value.weekStart);
  }, [isOpen, value]);

  useEffect(() => {
    if (isOpen) {
      firstDialogControlRef.current?.focus();
      return;
    }
    if (shouldRestoreFocusRef.current) {
      shouldRestoreFocusRef.current = false;
      triggerRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event: MouseEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) closePicker(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePicker();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [closePicker, isOpen]);

  const draftWeek = mondayOf(draftStart || value.weekStart) ?? value.weekStart;
  const draftWeekEnd = weekEnd(draftWeek) ?? draftWeek;
  const rangeInvalid =
    draftMode === 'range' &&
    (!draftStart || !draftEnd || draftEnd < draftStart || draftEnd > draftWeekEnd);
  const applyDisabled = !draftStart || rangeInvalid;

  const apply = (): void => {
    if (applyDisabled) return;
    const weekStart = mondayOf(draftStart);
    if (!weekStart) return;
    onChange({
      mode: draftMode,
      weekStart,
      capturedDate: draftMode === 'week' ? null : draftStart,
      capturedDateTo: draftMode === 'range' ? draftEnd : null,
    });
    closePicker();
  };

  return (
    <div className="rr-date-picker" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="rr-date-picker-trigger"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => {
          if (isOpen) closePicker();
          else setIsOpen(true);
        }}
      >
        <span>Date</span>
        <strong>{selectionLabel(value)}</strong>
      </button>
      {isOpen ? (
        <div className="rr-date-picker-popover" role="dialog" aria-label="Queue date">
          <div className="dg-scope-tabs" role="group" aria-label="Date mode">
            {(['week', 'day', 'range'] as const).map((mode) => (
              <button
                ref={mode === 'week' ? firstDialogControlRef : undefined}
                key={mode}
                type="button"
                className={draftMode === mode ? 'is-active' : ''}
                aria-pressed={draftMode === mode}
                onClick={() => setDraftMode(mode)}
              >
                {mode === 'range' ? 'Range' : mode[0].toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
          <label>
            <span>
              {draftMode === 'week' ? 'Any day in week' : draftMode === 'day' ? 'Day' : 'From'}
            </span>
            <input
              type="date"
              aria-label={
                draftMode === 'week' ? 'Week date' : draftMode === 'day' ? 'Day' : 'From date'
              }
              value={draftStart}
              onChange={(event) => {
                const next = event.target.value;
                setDraftStart(next);
                const nextWeek = mondayOf(next);
                const nextWeekEnd = nextWeek ? weekEnd(nextWeek) : null;
                if (
                  draftMode === 'range' &&
                  (!draftEnd || draftEnd < next || (nextWeekEnd !== null && draftEnd > nextWeekEnd))
                ) {
                  setDraftEnd(next);
                }
              }}
            />
          </label>
          {draftMode === 'range' ? (
            <label>
              <span>To</span>
              <input
                type="date"
                aria-label="To date"
                value={draftEnd}
                min={draftStart}
                max={draftWeekEnd}
                onChange={(event) => setDraftEnd(event.target.value)}
              />
            </label>
          ) : null}
          {rangeInvalid ? (
            <div className="rr-tool-hint is-warning">The range must stay inside one week.</div>
          ) : null}
          <div className="rr-date-picker-actions">
            <button type="button" className="dg-secondary-button" onClick={() => closePicker()}>
              Cancel
            </button>
            <button
              type="button"
              className="dg-primary-button"
              disabled={applyDisabled}
              onClick={apply}
            >
              Apply
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
